import { useEffect, useRef } from 'react'
import { requestScreenerHistoryPriority } from '@/lib/api'
import { canonicalExchangeScope } from '@/lib/screenerPriceExchange'
import { FALLBACK_EXCHANGE_CATALOG } from '@/lib/exchangeCatalog'
import type { ChartTimeRange } from '@/lib/screenerHistory'
import type { ScreenerHistoryRange } from '@/types'
const OI_VENUES = new Set(FALLBACK_EXCHANGE_CATALOG.filter(venue => venue.enabled && venue.features.open_interest).map(venue => venue.name))

/** Best-effort preparation hint; ordinary reads remain the only data source. */
export function useScreenerHistoryPriority(context: string, symbol: string, exchanges: string[],
  interval: string, range: ChartTimeRange, pending: ScreenerHistoryRange[] | undefined, enabled: boolean) {
  const venues = canonicalExchangeScope(exchanges).filter(venue => OI_VENUES.has(venue)).sort().join(',')
  const to = Math.floor(Math.min(Date.now() / 1000, range.to) / 60) * 60
  const from = Math.max(Math.floor(range.from / 60) * 60, to - 7 * 86400, 0)
  const scope = JSON.stringify([context, symbol.toUpperCase(), venues])
  const disabled = useRef<string | null>(null)
  const needed = enabled && interval !== '5s' && venues.length > 0 && Number.isFinite(from)
    && Number.isFinite(to) && to > from && !!pending?.some(value => value.from_ts < to && value.to_ts > from)
  useEffect(() => {
    if (disabled.current !== scope) disabled.current = null
    if (!needed || disabled.current === scope) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let controller: AbortController | undefined
    const enqueue = async () => {
      // Hidden tabs do not renew work priority for a chart nobody is viewing.
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        controller = new AbortController()
        try {
          const result = await requestScreenerHistoryPriority({ symbol: symbol.toUpperCase(),
            exchanges: venues.split(','), from_ts: from, to_ts: to }, controller.signal)
          if (stopped || controller.signal.aborted) return
          if (result.ok && result.data?.available === false) { disabled.current = scope; return }
        } catch { /* A preparation failure must never replace or clear chart data. */ }
      }
      if (!stopped) timer = setTimeout(enqueue, 30_000)
    }
    timer = setTimeout(enqueue, 180)
    return () => { stopped = true; if (timer !== undefined) clearTimeout(timer); controller?.abort() }
  }, [scope, needed, from, to])
}
